import React, { useEffect, useMemo, useState } from "react";
import { HandshakeIcon } from "../components/HandshakeIcon";
import { handshakeRain, rainCount } from "../lib/hotdogRain";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Trophy, Lock, CheckCircle2, Sparkles, Loader2, Handshake } from "lucide-react";
import { GradCard, SectionHeading } from "../components/wrestler/WrestlerUI";
import { PrestigeBackdrop } from "../components/leaderboard/LeaderboardUI";
import { HeroStat } from "../components/promotion/PromotionUI";


/* =========================================================
   Claim-burst — radial neon flash + ring + particles. Fires
   on the clicked card when the player claims an achievement.
   Same visual language as the Handshake Shop PurchaseBurst
   so unlocks across the whole game feel cohesive.
   ========================================================= */
function ClaimBurst({ color = "#FFE600", onDone }) {
  useEffect(() => {
    const t = setTimeout(() => onDone?.(), 1200);
    return () => clearTimeout(t);
  }, [onDone]);
  const particles = useMemo(() => (
    Array.from({ length: 24 }).map((_, i) => {
      const angle = (i / 24) * Math.PI * 2;
      const dist = 90 + Math.random() * 70;
      return {
        id: i,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        size: 4 + Math.random() * 6,
        delay: Math.random() * 60,
      };
    })
  ), []);
  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" aria-hidden="true">
      <div className="absolute inset-0" style={{
        background: `radial-gradient(circle at center, ${color}55 0%, transparent 60%)`,
        animation: "shop-flash 0.5s ease-out forwards",
      }} />
      <div className="absolute w-6 h-6 rounded-full" style={{
        border: `2px solid ${color}`,
        boxShadow: `0 0 20px ${color}`,
        animation: "shop-ring 0.8s ease-out forwards",
      }} />
      <Trophy size={56} style={{
        color, filter: `drop-shadow(0 0 18px ${color})`,
        animation: "shop-core 0.9s cubic-bezier(.1,.9,.3,1) forwards",
      }} />
      {particles.map(p => (
        <span key={p.id} className="absolute rounded-full" style={{
          width: p.size, height: p.size,
          background: color,
          boxShadow: `0 0 10px ${color}`,
          animation: `shop-particle 1.0s ${p.delay}ms cubic-bezier(.2,.7,.3,1) forwards`,
          "--dx": `${p.dx}px`, "--dy": `${p.dy}px`,
        }} />
      ))}
      <div className="absolute font-heading text-2xl sm:text-3xl font-black tracking-widest" style={{
        color, textShadow: `0 0 16px ${color}, 0 0 32px ${color}`,
        animation: "shop-banner 1.0s ease-out forwards",
      }}>CLAIMED!</div>
    </div>
  );
}

function AchievementCard({ a, busy, onClaim, burst, celebrating, delay }) {
  const isLocked = a.status === "locked";
  const isClaimable = a.status === "claimable";
  const isClaimed = a.status === "claimed";
  const pct = a.target > 0 ? Math.min(100, (a.progress / a.target) * 100) : 0;
  const color = isClaimable ? "#FFE600" : isClaimed ? "#39FF14" : "#9D00FF";
  const Icon = isClaimed ? CheckCircle2 : isClaimable ? Trophy : Lock;
  return (
    <div className="wr-rise h-full" style={{ animationDelay: `${delay}ms` }}>
    <div data-testid={`achievement-${a.id}`}
         className={`ach-card group relative h-full rounded-2xl p-[2px] transition-transform duration-300 hover:-translate-y-1 ${isClaimable ? "ach-ready" : ""} ${celebrating ? "scale-[1.03]" : ""}`}
         style={{ background: isLocked ? "linear-gradient(140deg, #4A3470, #2D1C52, #4A347088)" : `linear-gradient(140deg, ${color}, #5B3BFF66, ${color}88)`,
                  "--ach-glow": color,
                  boxShadow: celebrating ? `0 0 42px ${color}, 0 0 80px ${color}66` : undefined }}>
      <div className="relative h-full rounded-[14px] bg-[#110726]/95 p-3 overflow-hidden">
        {burst && <ClaimBurst color={color} onDone={() => {}} />}
        <div className="pointer-events-none absolute -right-8 -top-8 w-28 h-28 rounded-full blur-2xl opacity-25 transition-opacity duration-300 group-hover:opacity-60" style={{ background: color }} />
        {isClaimed && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full font-heading text-[10px] uppercase tracking-widest z-10 flex items-center gap-1"
               style={{ background: "#39FF14", color: "#0A051A", boxShadow: "0 0 8px #39FF14AA" }}><CheckCircle2 size={11} strokeWidth={3} /> CLAIMED</div>
        )}
        <div className="relative flex items-start gap-3">
          <div className="relative shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2" style={{ borderColor: isLocked ? "#4A3470" : color, boxShadow: isLocked ? "none" : `0 0 14px ${color}66` }}>
            <img src={`/achievements/${a.id}.webp`} alt={a.name} data-testid={`achievement-${a.id}-image`} loading="lazy"
                 className={`w-full h-full object-cover transition-[transform,filter] duration-500 group-hover:scale-110 ${isLocked ? "grayscale-[85%] brightness-75 group-hover:grayscale-0 group-hover:brightness-100" : ""}`} />
            <span className="wr-tile-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 opacity-0 group-hover:opacity-100 group-hover:animate-[wrTileShine_0.8s_ease-out]" />
            <div className="absolute bottom-1 left-1 w-6 h-6 rounded-md flex items-center justify-center bg-black/75 backdrop-blur border" style={{ borderColor: color }}>
              <Icon size={13} strokeWidth={2.6} style={{ color, filter: `drop-shadow(0 0 4px ${color})` }} />
            </div>
          </div>
          <div className="flex-1 min-w-0 pr-1">
            <div className="font-graffiti text-xl leading-tight pr-16" style={{ color: isLocked ? "#C9BDF0" : color, textShadow: isLocked ? "2px 2px 0 #000" : `0 0 10px ${color}88, 2px 2px 0 #000` }}>
              {a.name}
            </div>
            <div className="text-[11px] font-mono text-[#E5DCFF] mt-0.5">{a.condition}</div>
            <div className="text-[10px] font-mono italic text-[#A89BCC] mt-1 line-clamp-2">“{a.flavor}”</div>
            {!isClaimed && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full overflow-hidden bg-[#1a0a2a] border border-[#39194D]">
                  <div className="ach-bar h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, #FF007F)`, boxShadow: `0 0 8px ${color}` }} />
                </div>
                <span className="lb-num font-bold text-[11px] text-[#C9BDF0] shrink-0" data-testid={`achievement-${a.id}-progress`}>
                  {a.progress.toLocaleString()}/{a.target.toLocaleString()}
                </span>
              </div>
            )}
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-[#0A051A]/80 border border-[#FFE600]/40">
                <HandshakeIcon className="w-4 h-4" />
                <span className="lb-num font-bold text-sm" style={{ color: "#FFE600" }}>{a.reward_handshakes}</span>
              </div>
              {isClaimable && (
                <button data-testid={`achievement-${a.id}-claim-btn`} onClick={() => onClaim(a.id)} disabled={busy === a.id}
                        className="btn-neon px-5 py-1.5 text-[11px] font-bold disabled:opacity-50">
                  {busy === a.id ? <Loader2 className="inline animate-spin" size={12} /> : "CLAIM"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

export default function AchievementsPage() {
  const { refresh } = useAuth();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [burstFor, setBurstFor] = useState(null);
  const [celebrating, setCelebrating] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/achievements");
      setList(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Clear the "unseen claimable" notification badge on the Achievements nav
  // button the first time the user opens this page after any new unlock.
  useEffect(() => {
    api.post("/achievements/mark-seen")
      .then(() => refresh && refresh())
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claim = async (id) => {
    setBusy(id);
    const from = document.querySelector(`[data-testid='achievement-${id}-claim-btn']`)?.getBoundingClientRect();
    try {
      const r = await api.post(`/achievements/${id}/claim`);
      const rainP = handshakeRain(from, { count: rainCount(r.data.reward_handshakes) });
      setBurstFor(id);
      setCelebrating(id);
      toast.success(`🏆 ${r.data.name.toUpperCase()} — +${r.data.reward_handshakes} 🤝`, { duration: 4000 });
      // Hold the celebrating state for the burst duration, then refresh so the
      // card can slide to the "Claimed" section.
      setTimeout(async () => {
        setCelebrating(null);
        setBurstFor(null);
        await rainP;
        await refresh();
        await load();
      }, 1300);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const claimable = list.filter(a => a.status === "claimable");
  const locked = list.filter(a => a.status === "locked");
  const claimed = list.filter(a => a.status === "claimed");
  const totalReward = claimable.reduce((n, a) => n + a.reward_handshakes, 0);

  const renderSection = ({ title, count, items, color, icon, delay }) => (
    items.length > 0 ? (
      <GradCard delay={delay} className="mb-6" testId={`achievements-section-${title.toLowerCase().replace(/\s+/g,'-')}`}>
        <SectionHeading icon={icon} color={color}>{title} · <span className="lb-num">{count}</span></SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((a, i) => (
            <AchievementCard key={a.id} a={a} busy={busy} onClaim={claim} burst={burstFor === a.id} celebrating={celebrating === a.id} delay={delay + 60 + i * 50} />
          ))}
        </div>
      </GradCard>
    ) : null
  );

  return (
    <div className="relative max-w-5xl mx-auto px-4 py-8" data-testid="achievements-page">
      <PrestigeBackdrop />
      <div className="relative">
        <div className="wr-rise mb-6">          
          <h1 className="font-graffiti text-4xl sm:text-5xl lg:text-6xl leading-none" style={{ color: "#fff", textShadow: "0 0 22px #FF007FAA, 0 0 44px #00F0FF55, 3px 3px 0 #000" }}>ACHIEVEMENTS</h1>
        </div>
        <div className="grid grid-cols-3 gap-2.5 mb-6 max-w-2xl">
          <HeroStat icon={CheckCircle2} label="Claimed" color="#39FF14" testId="achievement-claimed-count" value={claimed.length} delay={80} />
          <HeroStat icon={Trophy} label="Ready" color="#FFE600" testId="achievement-ready-count" value={claimable.length} delay={160} />
          <HeroStat icon={Handshake} label="Waiting" color="#00F0FF" testId="achievement-waiting-reward" value={totalReward} delay={240} />
        </div>
        <div className="mb-6 max-w-2xl" data-testid="achievement-overall-progress">
          <div className="flex justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9BDF0] mb-1">
            <span>Completion</span><span className="lb-num font-bold text-[#39FF14]">{claimed.length}/{list.length}</span>
          </div>
          <div className="h-2.5 rounded-full overflow-hidden bg-[#1a0a2a] border border-[#39194D]">
            <div className="ach-bar h-full rounded-full" style={{ width: `${list.length ? (claimed.length / list.length) * 100 : 0}%`, background: "linear-gradient(90deg, #39FF14, #00F0FF, #FF007F)", boxShadow: "0 0 10px #39FF14" }} />
          </div>
        </div>

        {loading ? (
          <div className="text-center font-graffiti text-2xl neon-cyan animate-pulse">Loading achievements…</div>
        ) : (
          <>
            {renderSection({ title: "READY TO CLAIM", count: claimable.length, items: claimable, color: "#FFE600", icon: Trophy, delay: 120 })}
            {renderSection({ title: "IN PROGRESS", count: locked.length, items: locked, color: "#9D00FF", icon: Lock, delay: 200 })}
            {renderSection({ title: "COMPLETED", count: claimed.length, items: claimed, color: "#39FF14", icon: CheckCircle2, delay: 280 })}
          </>
        )}
      </div>
    </div>
  );
}
